import { ChoiceTree } from '@alkem/react-ui-choicetree';
import { ClickOutside } from '@alkem/react-ui-click-outside';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import './simple-select.scss';
import { onArrowDown, onArrowUp } from './tree-navigation';

export default class SimpleSelect extends PureComponent {
  static defaultProps = {
    disabled: false,
    autoSize: false,
  };

  constructor(props) {
    super(props);
    this.onClick = this.onClick.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.state = { isOpen: false, focusPath: [] };
    this.inputRef = createRef();
  }

  onClick() {
    if (this.props.disabled) {
      return;
    }
    if (this.state.isOpen) {
      this.hideDropdown();
    } else {
      this.onFocus();
    }
  }

  onSelect(value) {
    this.props.onSelect(value);
    this.handleClickOutside();
  }

  onFocus() {
    if (this.props.disabled) {
      return;
    }
    this.showDropdown();
  }

  onKeyDown(event) {
    const { options, disabled } = this.props;
    if (disabled) {
      return;
    }
    const { focusPath } = this.state;
    let nextState;
    event.preventDefault();
    switch (event.key) {
      case 'ArrowDown':
        nextState = onArrowDown({ children: options }, focusPath);
        break;
      case 'ArrowUp':
        nextState = onArrowUp({ children: options }, focusPath);
        break;
      case 'Escape':
        this.handleClickOutside();
        break;
      case 'Tab':
        this.handleClickOutside();
        break;
      case 'Enter':
        if (
          !!focusPath &&
          focusPath.length > 0 &&
          focusPath[0] < options.length
        ) {
          this.onSelect(options[focusPath[0]]);
        }
        break;
      default:
        return;
    }
    if (nextState) {
      this.setState(nextState);
    }
  }

  getValue() {
    const { placeholder, value } = this.props;
    if (value) {
      return value.label;
    } else if (placeholder) {
      return placeholder;
    }
    return '';
  }

  handleClickOutside() {
    this.hideDropdown();
  }

  showDropdown() {
    if (this.props.disabled) {
      return;
    }
    this.setState({ isOpen: true });
  }

  hideDropdown() {
    if (this.props.disabled) {
      return;
    }
    this.setState({ isOpen: false, focusPath: [] });
    if (this.inputRef.current) {
      this.inputRef.current.blur();
    }
  }

  render() {
    const {
      id,
      disabled,
      options,
      placeholder,
      value,
      extraOptions,
      autoSize,
      selectors,
    } = this.props;
    const { isOpen, focusPath } = this.state;
    const contentClass = {
      SimpleSelect__content: true,
      'SimpleSelect__content--disabled': !!disabled,
    };
    const valueClass = {
      SimpleSelect_value: true,
      'SimpleSelect_value--placeholder': !value && !!placeholder,
    };
    const chevronClass = {
      SimpleSelect_chevron: true,
      mdi: true,
      'mdi-chevron-down': !isOpen,
      'mdi-chevron-up': !!isOpen,
    };

    return (
      <ClickOutside
        id={id ? `SimpleSelect-${id}` : null}
        className="SimpleSelect"
        onClick={this.onClick}
        onClickOutside={this.handleClickOutside}
      >
        <div className={classNames(contentClass)}>
          <span className={classNames(valueClass)}>{this.getValue()}</span>
          <i className={classNames(chevronClass)} />
          <input
            ref={this.inputRef}
            className="SimpleSelect__input"
            maxLength="0"
            onKeyDown={this.onKeyDown}
            onFocus={this.onFocus}
            disabled={disabled}
          />
        </div>
        {autoSize && (
          <div className="SimpleSelect__autoSize">
            {options.map((opt) => (
              <div key={JSON.stringify(opt)} className="SimpleSelect__content">
                <span className="SimpleSelect_value">{opt.label}</span>
                <i className="SimpleSelect_chevron mdi mdi-chevron-down" />
              </div>
            ))}
          </div>
        )}
        {isOpen && (
          <ChoiceTree
            id={`SimpleSelect-choiceTree-${id}`}
            options={options}
            onSelect={this.onSelect}
            focusPath={focusPath}
            extraOptions={extraOptions}
            selectors={selectors}
          />
        )}
      </ClickOutside>
    );
  }
}

SimpleSelect.propTypes = {
  options: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  value: PropTypes.object,
  placeholder: PropTypes.string,
  extraOptions: PropTypes.object,
  autoSize: PropTypes.bool,
  selectors: PropTypes.shape({
    selectId: PropTypes.func.isRequired,
    selectLabel: PropTypes.func.isRequired,
  }),
};
